import React, { useEffect, useState, Component } from 'react';
import { Fuel, Droplets, Flame, TrendingUp, TrendingDown, Equal } from 'lucide-react';
import { AnimateOnScroll } from './AnimateOnScroll';
// Datos oficiales (7 al 13 de febrero 2026)
// trend: 0 (igual), 1 (sube), -1 (baja)
const initialPrices = [{
  name: 'Gas Licuado (GLP)',
  price: 'RD $137.20',
  icon: Flame,
  color: '#FCEE02',
  trend: 0
}, {
  name: 'Gasolina Premium',
  price: 'RD $290.10',
  icon: Fuel,
  color: '#EC2424',
  trend: 0
}, {
  name: 'Gasolina Regular',
  price: 'RD $272.50',
  icon: Fuel,
  color: '#FB900D',
  trend: 0
}, {
  name: 'Gasoil Óptimo',
  price: 'RD $242.10',
  icon: Droplets,
  color: '#EC2424',
  trend: 0
}, {
  name: 'Gasoil Regular',
  price: 'RD $224.80',
  icon: Droplets,
  color: '#FB900D',
  trend: 0
}];
export function PricesSection() {
  const [weekRange, setWeekRange] = useState('');
  useEffect(() => {
    const calculateWeek = () => {
      const now = new Date();
      const day = now.getDay();
      const hour = now.getHours();
      let startDate = new Date(now);
      // Ajuste de vigencia MICM: Sábados a Viernes
      const diffToSaturday = day === 6 ? 0 : day >= 5 && hour >= 14 ? 6 - day : -(day + 1);
      startDate.setDate(now.getDate() + diffToSaturday);
      let endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: 'short'
      };
      setWeekRange(`${startDate.toLocaleDateString('es-DO', options)} - ${endDate.toLocaleDateString('es-DO', options)} 2026`);
    };
    calculateWeek();
  }, []);
  // Componente de Indicador Visual de Tendencia
  const PriceIndicator = ({
    trend
  }: {
    trend: number;
  }) => {
    if (trend === 1) {
      // SUBE (Mal/Rojo)
      return <div className="flex flex-col items-center text-red-500 animate-pulse" data-id="element-273">
          <TrendingUp className="w-6 h-6" data-id="element-274" />
          <div className="h-4 w-px bg-red-500 mt-1 rotate-45 transform origin-top" data-id="element-275" />
        </div>;
    }
    if (trend === -1) {
      // BAJA (Bueno/Verde)
      return <div className="flex flex-col items-center text-green-500 animate-bounce" data-id="element-276">
          <TrendingDown className="w-6 h-6" data-id="element-277" />
          <div className="h-4 w-px bg-green-500 mt-1 -rotate-45 transform origin-top" data-id="element-278" />
        </div>;
    }
    // IGUAL (Estable/Verde)
    return <div className="flex items-center justify-center text-green-500/80" data-id="element-279">
        <Equal className="w-6 h-6" data-id="element-280" />
      </div>;
  };
  const shineEffect = 'before:absolute before:inset-0 before:-translate-x-full before:group-hover:translate-x-full before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:transition-transform before:duration-1000 overflow-hidden';
  return <section className="py-16 sm:py-24 relative transition-colors duration-300" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-281">
      <div className="container mx-auto px-4 relative z-10" data-id="element-282">
        <AnimateOnScroll data-id="element-283">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-284">
            <div className="max-w-2xl" data-id="element-285">
              <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4" style={{
              color: 'var(--text-primary)'
            }} data-id="element-286">
                <span style={{
                color: 'var(--accent-yellow-text)'
              }} data-id="element-287">
                  Precios
                </span>{' '}
                Hoy
              </h2>
              <p className="text-xl border-l-4 border-[#EC2424] pl-6 text-gray-400" data-id="element-288">
                Monitoreo oficial MICM para República Dominicana.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 px-6 py-3 rounded-xl font-mono text-sm uppercase tracking-widest text-gray-300 shadow-xl" data-id="element-289">
              <span className="text-[#FCEE02] mr-2" data-id="element-290">●</span> Vigencia:{' '}
              {weekRange || '07 Feb - 13 Feb 2026'}
            </div>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-291">
          {/* PRODUCTO PRINCIPAL: GLP */}
          <AnimateOnScroll className="lg:col-span-5 lg:row-span-2" direction="left" data-id="element-292">
            <div className={`h-full bg-[#FCEE02] p-1 group shadow-2xl relative rounded-2xl ${shineEffect}`} data-id="element-293">
              <div className="h-full p-10 flex flex-col justify-between relative z-10 rounded-xl" style={{
              backgroundColor: '#0F0F0F'
            }} data-id="element-294">
                <div className="flex justify-between items-start" data-id="element-295">
                  <div className="p-4 bg-[#FCEE02]/5 rounded-2xl border border-[#FCEE02]/20" data-id="element-296">
                    <Flame className="w-12 h-12 text-[#FCEE02]" data-id="element-297" />
                  </div>
                  <PriceIndicator trend={initialPrices[0].trend} data-id="element-298" />
                </div>
                <div className="mt-20" data-id="element-299">
                  <h3 className="text-3xl font-black text-white uppercase mb-2" data-id="element-300">
                    {initialPrices[0].name}
                  </h3>
                  <div className="flex items-baseline gap-3" data-id="element-301">
                    <span className="text-6xl font-black text-[#FCEE02] tracking-tighter" data-id="element-302">
                      {initialPrices[0].price}
                    </span>
                    <span className="text-gray-500 font-bold" data-id="element-303">GALÓN</span>
                  </div>
                </div>
              </div>
            </div>
          </AnimateOnScroll>

          {/* GRID COMBUSTIBLES LÍQUIDOS */}
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-304">
            {initialPrices.slice(1).map((fuel, index) => <AnimateOnScroll key={index} delay={index * 0.1} direction="right" data-id="element-305">
                <div className={`group p-8 border transition-all duration-500 relative rounded-xl ${shineEffect}`} style={{
              backgroundColor: 'var(--card-bg)',
              borderColor: 'var(--border-color)'
            }} data-id="element-306">
                  <div className="absolute top-0 left-0 w-1.5 h-full transition-all duration-500 group-hover:w-3" style={{
                backgroundColor: fuel.color
              }} data-id="element-307" />

                  <div className="flex justify-between items-start mb-10" data-id="element-308">
                    <h3 className="text-xl font-black uppercase max-w-[120px]" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-309">
                      {fuel.name}
                    </h3>
                    <PriceIndicator trend={fuel.trend} data-id="element-310" />
                  </div>

                  <div className="flex justify-between items-end" data-id="element-311">
                    <span className="text-4xl font-black tracking-tight" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-312">
                      {fuel.price}
                    </span>
                    <fuel.icon className="w-10 h-10 text-white/5 group-hover:text-white/10 transition-colors" data-id="element-313" />
                  </div>
                </div>
              </AnimateOnScroll>)}
          </div>
        </div>
      </div>
    </section>;
}